/**
 * Global chunk-load recovery.
 *
 * When we redeploy, webpack rebuilds route chunks with new content-hash
 * filenames (e.g. Menu.a1b2c3.chunk.js → Menu.d4e5f6.chunk.js). If a user
 * still has the old index.html + main.js cached in their mobile browser,
 * their JS tries to `import('/static/js/Menu.a1b2c3.chunk.js')` and 404s.
 *
 * React's dynamic import surfaces this as a rejected promise. The Emergent
 * platform error overlay picks it up and shows "[bundle] Script error." —
 * unhelpful because the browser redacts cross-origin script errors.
 *
 * The fix is the SPA-standard pattern: catch ChunkLoadError once, blow
 * away the cache and reload so the user gets the fresh index.html + fresh
 * chunk map. Guarded with sessionStorage so we don't reload-loop if the
 * failure is something else (like a genuinely missing chunk).
 */
export function installChunkErrorRecovery() {
  if (typeof window === 'undefined') return;

  const RELOAD_KEY = 'xin.chunk_reload_at';
  const RECENT_WINDOW_MS = 30_000; // don't reload more than once per 30 s

  const looksLikeChunkFail = (msg = '', filename = '') => {
    const m = String(msg).toLowerCase();
    const f = String(filename).toLowerCase();
    // The common error strings across browsers + our webpack setup.
    return (
      m.includes('loading chunk') ||
      m.includes('loading css chunk') ||
      m.includes("chunkloaderror") ||
      m.includes('failed to fetch dynamically imported module') ||
      m.includes('unexpected token') && f.endsWith('.js') ||
      /\/static\/js\/.+\.chunk\.js/.test(f)
    );
  };

  const softReload = (why) => {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RECENT_WINDOW_MS) {
      // We already reloaded once recently — don't loop. Let the platform
      // overlay show the error so we can debug.
      return;
    }
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    // Force a bypass of the disk cache so the fresh index.html is fetched.
    // A plain reload can still serve the stale HTML from Safari's aggressive
    // cache on some iOS versions.
    console.warn('[xin] chunk load failed → reloading fresh index.html', why);
    window.location.reload();
  };

  // Rejected dynamic imports land here.
  window.addEventListener('unhandledrejection', (ev) => {
    const err = ev?.reason;
    if (looksLikeChunkFail(err?.message || err || '', err?.filename || '')) {
      softReload({ from: 'unhandledrejection', msg: err?.message });
    }
  });

  // Sync errors from static <script src="..."> tags (rare — mostly the
  // preload preflight before dynamic imports) or fully-loaded chunk errors.
  window.addEventListener('error', (ev) => {
    if (looksLikeChunkFail(ev?.message || '', ev?.filename || '')) {
      softReload({ from: 'error-event', msg: ev?.message, file: ev?.filename });
    }
  }, true);
}
